import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToSection = (id: string, path: string = "/") => {
    setMobileOpen(false);
    if (location.pathname !== path) {
      navigate(path, { state: { scrollTo: id } });
      return;
    }
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const navLinks = [
    { label: "Home", href: "/", scrollId: "hero" },
    { label: "About", href: "/about", scrollId: "about-vantage" },
    { label: "Services", href: "/services", scrollId: "advisory-services" },
    { label: "AI Governance", href: "/", scrollId: "governance" },
    { label: "Contact", href: "/contact", scrollId: "get-in-touch" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-bg-section/95 backdrop-blur-sm border-b border-border"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <nav className="flex items-center justify-between h-[72px]">
          <Link to="/" className="flex items-center">
            <span className="font-serif font-semibold text-xl text-text-headline tracking-tight">
              VantageSight
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((item) =>
              item.scrollId ? (
                <button
                  key={item.label}
                  onClick={() => scrollToSection(item.scrollId!, item.href)}
                  className="font-sans text-[15px] text-text-muted hover:text-primary transition-colors cursor-pointer whitespace-nowrap"
                >
                  {item.label}
                </button>
              ) : (
                <Link
                  key={item.label}
                  to={item.href}
                  className="font-sans text-[15px] text-text-muted hover:text-primary transition-colors whitespace-nowrap"
                >
                  {item.label}
                </Link>
              )
            )}
          </div>

          <div className="hidden md:flex items-center gap-4">
            <a
              href="mailto:smurray@vantagesightai.com"
              className="font-sans text-[15px] text-primary hover:text-primary-light transition-colors whitespace-nowrap"
            >
              <i className="ri-mail-line w-5 h-5 flex items-center justify-center inline-flex mr-1"></i>
              Get in Touch
            </a>
            <Link
              to="/contact"
              className="font-sans text-[15px] font-medium text-white bg-primary hover:bg-primary-light px-6 py-2.5 rounded-full transition-colors whitespace-nowrap cursor-pointer"
            >
              Book a Call
            </Link>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-text-headline cursor-pointer"
            aria-label="Toggle menu"
          >
            <i
              className={`ri-${mobileOpen ? "close" : "menu"}-line text-2xl w-6 h-6 flex items-center justify-center`}
            ></i>
          </button>
        </nav>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-bg-section border-t border-border shadow-lg">
          <div className="px-4 py-6 flex flex-col gap-4">
            {navLinks.map((item) =>
              item.scrollId ? (
                <button
                  key={item.label}
                  onClick={() => scrollToSection(item.scrollId!, item.href)}
                  className="font-sans text-[16px] text-text-muted hover:text-primary text-left cursor-pointer"
                >
                  {item.label}
                </button>
              ) : (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={() => setMobileOpen(false)}
                  className="font-sans text-[16px] text-text-muted hover:text-primary"
                >
                  {item.label}
                </Link>
              )
            )}
            <div className="flex flex-col gap-3 pt-4 border-t border-border">
              <Link
                to="/contact"
                onClick={() => setMobileOpen(false)}
                className="font-sans text-[15px] font-medium text-white bg-primary hover:bg-primary-light px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap text-center"
              >
                Book a Call
              </Link>
              <a
                href="mailto:smurray@vantagesightai.com"
                className="font-sans text-[15px] text-primary hover:text-primary-light transition-colors text-center"
              >
                <i className="ri-mail-line w-5 h-5 flex items-center justify-center inline-flex mr-1"></i>
                Get in Touch
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}